// src/pages/Configuration.tsx
import React, { useState, useEffect } from 'react';
import { Save, TestTube, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { configurationService, UpdateConfigurationRequest } from '../services/configuration';
import { settingsService } from '../services/settings';
import { CompanyConfig } from '../types';

const Configuration: React.FC = () => {
  // Loaded config from API
  const [config, setConfig] = useState<CompanyConfig | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Send method (stored in Settings table, not Company)
  const [sendMethod, setSendMethod] = useState<'SmtpRelay' | 'Direct'>('SmtpRelay');
  const [savedSendMethod, setSavedSendMethod] = useState<'SmtpRelay' | 'Direct'>('SmtpRelay');

  // Editable form state (mirrors config but allows local edits)
  const [smtpServer, setSmtpServer] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpUseSsl, setSmtpUseSsl] = useState(true);

  const [dkimDomain, setDkimDomain] = useState('');
  const [dkimSelector, setDkimSelector] = useState('');
  const [dkimPrivateKey, setDkimPrivateKey] = useState('');

  const [emailsPerHour, setEmailsPerHour] = useState(1000);
  const [emailsPerDay, setEmailsPerDay] = useState(10000);

  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  // UI state
  const [isSaving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  useEffect(() => {
    loadConfiguration();
  }, []);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const loadConfiguration = async () => {
    setLoading(true);
    setLoadError('');
    try {
      // Load company config and send method in parallel
      const [companyConfig, queueStatus] = await Promise.all([
        configurationService.getConfiguration(),
        settingsService.getQueueStatus()
      ]);

      setConfig(companyConfig);

      // Populate form fields from loaded config
      setSmtpServer(companyConfig.smtpServer || '');
      setSmtpPort(companyConfig.smtpPort || 587);
      setSmtpUsername(companyConfig.smtpUsername || '');
      setSmtpUseSsl(companyConfig.smtpUseSsl);
      setEmailsPerHour(companyConfig.emailsPerHour);
      setEmailsPerDay(companyConfig.emailsPerDay);
      setDkimDomain(companyConfig.dkimDomain || '');
      setDkimSelector(companyConfig.dkimSelector || '');
      setWebhookUrl(companyConfig.webhookUrl || '');
      // Passwords/secrets are never loaded - fields stay empty

      setSendMethod(queueStatus.sendMethod);
      setSavedSendMethod(queueStatus.sendMethod);
    } catch (error) {
      console.error('Failed to load configuration:', error);
      setLoadError('Failed to load configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // Save send method if it changed
      if (sendMethod !== savedSendMethod) {
        await settingsService.updateSendMethod(sendMethod);
        setSavedSendMethod(sendMethod);
      }

      // Build the update request
      const request: UpdateConfigurationRequest = {
        smtpServer: smtpServer || null,
        smtpPort: smtpPort,
        smtpUsername: smtpUsername || null,
        smtpUseSsl: smtpUseSsl,
        dkimDomain: dkimDomain || null,
        dkimSelector: dkimSelector || null,
        emailsPerHour: emailsPerHour,
        emailsPerDay: emailsPerDay,
        webhookUrl: webhookUrl || null,
      };

      // Only include sensitive fields if the user entered a new value
      if (smtpPassword) request.smtpPassword = smtpPassword;
      if (dkimPrivateKey) request.dkimPrivateKey = dkimPrivateKey;
      if (webhookSecret) request.webhookSecret = webhookSecret;

      const updated = await configurationService.updateConfiguration(request);
      setConfig(updated);

      // Clear password/secret fields after successful save
      setSmtpPassword('');
      setDkimPrivateKey('');
      setWebhookSecret('');

      showMessage('success', 'Configuration saved successfully');
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || 'Failed to save configuration';
      showMessage('error', errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!smtpServer) {
      showMessage('error', 'Enter an SMTP server before testing');
      return;
    }
    try {
      const result = await configurationService.testSmtpConnection({
        smtpServer,
        smtpPort,
        smtpUsername,
        smtpPassword,
        smtpUseSsl
      });
      showMessage(result.success ? 'success' : 'error', result.message);
    } catch (error: any) {
      showMessage('error', error.response?.data?.message || 'SMTP connection test failed');
    }
  };

  const isSmtpDisabled = sendMethod === 'Direct';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-gray-400" size={32} />
        <span className="ml-3 text-gray-500">Loading configuration...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="text-red-400 mb-3" size={32} />
        <p className="text-red-600 mb-4">{loadError}</p>
        <button
          onClick={loadConfiguration}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuration</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage SMTP, DKIM, rate limiting, and webhook settings for {config?.companyName || 'your company'}
        </p>
      </div>

      {/* Status message */}
      {message && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg ${
          message.type === 'success'
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Send Method */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-4">Send Method</h2>

        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Send Method
          </label>
          <select
            value={sendMethod}
            onChange={(e) => setSendMethod(e.target.value as 'SmtpRelay' | 'Direct')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="SmtpRelay">SMTP Relay (Default)</option>
            <option value="Direct">Direct Send</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            SMTP Relay uses the configured SMTP server below.
            Direct Send connects directly to recipient mail servers via MX lookup.
          </p>
        </div>
      </div>

      {/* SMTP Configuration */}
      <div className={`bg-white rounded-lg shadow-sm p-6 ${isSmtpDisabled ? 'opacity-50' : ''}`}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold">SMTP Configuration</h2>
            {isSmtpDisabled && (
              <p className="text-sm text-amber-600 mt-1">
                Not required when using Direct Send
              </p>
            )}
          </div>
          <button
            onClick={handleTestSmtp}
            disabled={isSmtpDisabled}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TestTube size={16} />
            Test Connection
          </button>
        </div>

        <fieldset disabled={isSmtpDisabled}>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SMTP Server
              </label>
              <input
                type="text"
                value={smtpServer}
                onChange={(e) => setSmtpServer(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="smtp.example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SMTP Port
              </label>
              <input
                type="number"
                value={smtpPort}
                onChange={(e) => setSmtpPort(parseInt(e.target.value) || 25)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                value={smtpUsername}
                onChange={(e) => setSmtpUsername(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                value={smtpPassword}
                onChange={(e) => setSmtpPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder={config?.smtpUsername ? '(unchanged)' : ''}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to keep the existing password
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="smtpUseSsl"
              checked={smtpUseSsl}
              onChange={(e) => setSmtpUseSsl(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="smtpUseSsl" className="text-sm text-gray-700">
              Use SSL/TLS
            </label>
          </div>
        </fieldset>
      </div>

      {/* DKIM Configuration */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-4">DKIM Configuration</h2>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              DKIM Domain
            </label>
            <input
              type="text"
              value={dkimDomain}
              onChange={(e) => setDkimDomain(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              DKIM Selector
            </label>
            <input
              type="text"
              value={dkimSelector}
              onChange={(e) => setDkimSelector(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="mail"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            DKIM Private Key
          </label>
          {config?.hasDkimKey && !dkimPrivateKey && (
            <p className="text-xs text-green-600 mb-1">
              A DKIM key is currently configured
            </p>
          )}
          <textarea
            value={dkimPrivateKey}
            onChange={(e) => setDkimPrivateKey(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono text-xs"
            placeholder={config?.hasDkimKey ? '(paste new key to replace)' : '-----BEGIN RSA PRIVATE KEY-----'}
          />
          <p className="text-xs text-gray-500 mt-1">
            Paste your DKIM private key in PEM format. Leave blank to keep the existing key.
          </p>
        </div>
      </div>

      {/* Rate Limiting */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-4">Rate Limiting</h2>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Emails per Hour
            </label>
            <input
              type="number"
              value={emailsPerHour}
              onChange={(e) => setEmailsPerHour(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum emails this company can send per hour
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Emails per Day
            </label>
            <input
              type="number"
              value={emailsPerDay}
              onChange={(e) => setEmailsPerDay(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum emails this company can send per day
            </p>
          </div>
        </div>
      </div>

      {/* Webhook Notifications */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-1">Webhook Notifications</h2>
        <p className="text-sm text-gray-500 mb-4">
          MailSend will POST status updates (bounces, blocks, unblocks) to this URL.
          Requests are signed with HMAC-SHA256 using the secret below.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Webhook URL
            </label>
            <input
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://yourapp.com/api/mailsend-webhook"
            />
            <p className="text-xs text-gray-500 mt-1">
              Must be an HTTPS endpoint that returns 2xx on success
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Webhook Secret
            </label>
            {config?.hasWebhookSecret && !webhookSecret && (
              <p className="text-xs text-green-600 mb-1">
                A webhook secret is currently configured
              </p>
            )}
            <input
              type="password"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder={config?.hasWebhookSecret ? '(unchanged)' : 'Enter a shared secret'}
            />
            <p className="text-xs text-gray-500 mt-1">
              Used to sign webhook payloads via the X-MailSend-Signature header.
              Leave blank to keep the existing secret.
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
        >
          {isSaving ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Save size={18} />
          )}
          {isSaving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
};

export default Configuration;
